import moment from 'moment';
import { ActionTree } from 'vuex';
import { actionLoadFields } from '@signing-page/fields/store/field.actions';
import service from '@signing-page/api/documents/services/document.service';
import { IDocument } from '@signing-page/documents/types/document';
import { IDocumentRevision } from '@signing-page/documents/types/document-revision';
import { EDocumentStatus } from '@signing-page/documents/types/enums/document-status';
import { ERecipientStatus } from '@signing-page/recipient/types/enums/recipient-status';
import { IForwardRecipientInfo } from '@signing-page/signing/types/forward-recipient-info';
import {
  mutationSetAttachmentView,
  mutationSetDocument,
  mutationSetDocumentAttachments,
  mutationSetDocumentSignedDate,
  mutationSetDocumentStatus,
  mutationSetFocusedRequiredId,
  mutationSetLoading,
  mutationSetRecipientStatus,
  mutationSetUploadingAttachment,
  mutationRemoveUploadingAttachment,
  mutationSetError,
  mutationSetForwarded,
  mutationUpdateRecipients,
  mutationSetButtonLoading,
  mutationSetPdfUrl,
  mutationSetRevisionsLoading,
  mutationSetDocumentRevisions,
  mutationSetRevisionsViewedLoading,
} from '@signing-page/documents/store/document.mutations';
import { IRootState } from '@signing-page/store/store';
import { namespace as fieldNamespace } from '@signing-page/fields/store/field.store';
import { IDocumentState } from '@signing-page/documents/store/document.store';
import { EDocumentEvent } from '@signing-page/documents/types/enums/document-event';
import { IUploadAttachment } from '@signing-page/attachments/types/upload-attachment';
import { AttachmentService } from '@signing-page/api/attachments/services/attachment.service';
import { IDocumentAttachment } from '@/types/document-attachment';
import { translate } from '@/helpers/translation.helper';
import { IRecipient } from '@/types/Recipient';
import { getRecipientById } from '@/common/helpers';

export const actionLoadDocument = 'loadDocument';
export const actionLoadDocumentSuccess = 'loadDocumentSuccess';
export const actionLoadDocumentFailed = 'loadDocumentFailed';

export const actionLoadDocumentRevisions = 'loadDocumentRevisions';
export const actionLoadDocumentRevisionsSuccess = 'loadDocumentRevisionsSuccess';
export const actionLoadDocumentRevisionsFailed = 'loadDocumentRevisionsFailed';

export const actionSetRevisionViewedByRecipient = 'setRevisionViewedByRecipient';
export const actionSetRevisionViewedByRecipientSuccess = 'setRevisionViewedByRecipientSuccess';
export const actionSetRevisionViewedByRecipientFailed = 'setRevisionViewedByRecipientFailed';

export const actionSetFocusedRequiredId = 'setFocusedRequiredId';
export const actionCompleteSigning = 'completeSigning';

export const actionUpdateAttachments = 'updateAttachments';
export const actionSetAttachmentView = 'setAttachmentView';

export const actionRejectDocument = 'rejectDocument';

export const actionLoadAfterSign = 'loadAfterSign';

export const actionSendPusherStatus = 'sendPusherStatus';
export const actionSendPusherStatusSuccess = 'sendPusherStatusSuccess';

export const actionSendEventLog = 'sendEventLog';

export const actionSendDocumentStatus = 'sendDocumentStatus';
export const actionSetRecipientStatus = 'setRecipientStatus';

export const actionUploadAttachment = 'uploadAttachment';
export const actionUploadAttachmentFailed = 'uploadAttachmentFailed';

export const actionApproveDocument = 'approveDocument';
export const actionExpiredReload = 'expiredReload';

export const actionForwardToRecipient = 'forwardToRecipient';
export const actionForwardToRecipientFailed = 'forwardToRecipientFailed';
export const actionForwardToRecipientSuccess = 'forwardToRecipientSuccess';
export const actionSetForwardStatus = 'setForwardStatus';
export const actionGetPdfUrl = 'getPdfPrintUrl';
export const actionGetPdfUrlSuccess = 'getPdfPrintUrlSuccess';
export const actionGetPdfUrlFailed = 'getPdfPrintUrlFailed';
export const actionSetDocumentStatus = 'setDocumentStatus';

export const actions: ActionTree<IDocumentState, IRootState> = {
  async [actionLoadDocument]({ commit, dispatch }) {
    commit(mutationSetLoading, true);
    try {
      const document: IDocument = await service.getDocument();
      dispatch(actionLoadDocumentSuccess, document);
    } catch (error) {
      dispatch(actionLoadDocumentFailed);
    }
  },
  async [actionLoadDocumentSuccess]({ commit, dispatch }, document: IDocument) {
    commit(mutationSetDocument, document);
    dispatch(actionExpiredReload, document);
    commit(mutationSetLoading, false);
  },
  async [actionLoadDocumentFailed]({ commit, dispatch }) {
    commit(mutationSetLoading, false);
    commit(mutationSetError, true);
    dispatch('alert/showDangerAlert', translate('failed-to-load-document'), {
      root: true,
    });
  },
  async [actionLoadDocumentRevisions]({ commit, dispatch }) {
    commit(mutationSetRevisionsLoading, true);
    try {
      const { documentRevisions } = await service.getDocumentRevisions();
      dispatch(actionLoadDocumentRevisionsSuccess, documentRevisions);
    } catch (error) {
      dispatch(actionLoadDocumentRevisionsFailed);
    }
  },
  [actionLoadDocumentRevisionsSuccess]({ commit }, documentRevisions: IDocumentRevision[]) {
    commit(mutationSetDocumentRevisions, documentRevisions);
    commit(mutationSetRevisionsLoading, false);
  },
  [actionLoadDocumentRevisionsFailed]({ commit, dispatch }) {
    commit(mutationSetRevisionsLoading, false);
    commit(mutationSetError, true);
    dispatch('alert/showDangerAlert', translate('failed-to-load-document-revision'), {
      root: true,
    });
  },

  async [actionSetRevisionViewedByRecipient]({ commit, dispatch }, revision: number) {
    commit(mutationSetRevisionsViewedLoading, true);
    try {
      await service.addRecipientView(revision);
      dispatch(actionSetRevisionViewedByRecipientSuccess);
    } catch (error) {
      dispatch(actionSetRevisionViewedByRecipientFailed);
    }
  },
  [actionSetRevisionViewedByRecipientSuccess]({ commit }) {
    commit(mutationSetRevisionsViewedLoading, false);
  },
  [actionSetRevisionViewedByRecipientFailed]({ commit, dispatch }) {
    commit(mutationSetRevisionsViewedLoading, false);
    commit(mutationSetError, true);
    dispatch('alert/showDangerAlert', translate('failed-to-set-revision-viewed'), {
      root: true,
    });
  },
  async [actionSetFocusedRequiredId]({ commit }, focusedRequiredId: string) {
    commit(mutationSetFocusedRequiredId, focusedRequiredId);
  },
  [actionCompleteSigning](
    { commit, dispatch },
    { signeesWaiting, recipientId }: { signeesWaiting: number; recipientId: string }
  ) {
    const status: ERecipientStatus = ERecipientStatus.Signed;
    commit(mutationSetRecipientStatus, { recipientId, status });
    if (signeesWaiting === 0) {
      dispatch('alert/showInfoAlert', translate('loading-document-certificate'), { root: true });
      dispatch(actionSendPusherStatus, { status: EDocumentStatus.Signed });
      commit(mutationSetDocumentSignedDate, moment().toISOString());
    }
  },
  async [actionSetAttachmentView]({ commit }, id: string) {
    commit(mutationSetAttachmentView, id);
  },
  async [actionUpdateAttachments]({ commit }, document: IDocument) {
    const oldAttachments: IDocumentAttachment[] = (document && document.documentAttachments) || [];
    try {
      const documentAttachments: IDocumentAttachment[] = await service.getAttachments();
      commit(mutationSetDocumentAttachments, documentAttachments);
    } catch (error) {
      commit(mutationSetDocumentAttachments, oldAttachments);
    }
  },
  async [actionRejectDocument](
    _,
    {
      reason,
      recipientId,
      documentId,
      authId,
    }: { reason: string; recipientId: string; documentId: string; authId: string }
  ) {
    await service.reject(reason, documentId, recipientId, authId);
  },
  async [actionLoadAfterSign]({ dispatch }) {
    dispatch(actionLoadDocument);
    dispatch(`${fieldNamespace}/${actionLoadFields}`, {});
  },
  async [actionSendDocumentStatus](
    { dispatch, state, rootState, commit },
    { newDocumentStatus }: { newDocumentStatus: EDocumentStatus }
  ) {
    if (rootState.isPreview) {
      return;
    }
    const {
      document: { recipients, status },
    }: IDocumentState = state;
    const recipient = getRecipientById(rootState.recipientId, recipients);

    if (status === newDocumentStatus && status === recipient.status) {
      return;
    }
    switch (newDocumentStatus) {
      case EDocumentStatus.Reviewed:
        await dispatch(actionSendPusherStatus, {
          status: EDocumentEvent.Reviewed,
        });
        await dispatch(actionSendEventLog, { event: EDocumentEvent.Reviewed });
        commit(mutationSetDocumentStatus, EDocumentStatus.Reviewed);
        break;
      case EDocumentStatus.Viewed:
        await dispatch(actionSendPusherStatus, {
          status: EDocumentEvent.Viewed,
        });
        commit(mutationSetDocumentStatus, EDocumentStatus.Viewed);
        break;
      case EDocumentStatus.Rejected:
        await dispatch(actionSendPusherStatus, {
          status: EDocumentEvent.Rejected,
        });
        await dispatch(actionSendEventLog, { event: EDocumentEvent.Rejected });
        commit(mutationSetDocumentStatus, EDocumentStatus.Rejected);
        break;
    }
  },
  async [actionSetRecipientStatus](
    { dispatch, rootState, state, commit },
    { status }: { status: ERecipientStatus }
  ) {
    const { recipientId }: IRootState = rootState;
    const {
      document: { recipients },
    }: IDocumentState = state;
    const recipient = getRecipientById(recipientId, recipients);
    switch (status) {
      case ERecipientStatus.Reviewed:
        commit(mutationSetRecipientStatus, {
          recipientId: recipient.id,
          status: ERecipientStatus.Reviewed,
        });
        await dispatch(actionSendPusherStatus, {
          status: EDocumentEvent.Reviewed,
        });
        await dispatch(actionSendEventLog, { event: EDocumentEvent.Reviewed });
        break;
      case ERecipientStatus.Viewed:
        commit(mutationSetRecipientStatus, {
          recipientId: recipient.id,
          status: ERecipientStatus.Viewed,
        });
        await dispatch(actionSendPusherStatus, {
          status: EDocumentEvent.Viewed,
        });
        break;
      case ERecipientStatus.Rejected:
        commit(mutationSetRecipientStatus, {
          recipientId: recipient.id,
          status: ERecipientStatus.Rejected,
        });
        await dispatch(actionSendPusherStatus, {
          status: EDocumentEvent.Rejected,
        });
        await dispatch(actionSendEventLog, { event: EDocumentEvent.Rejected });
        break;
    }
  },
  async [actionSendPusherStatus]({ rootState, state }, { status }: { status: EDocumentEvent }) {
    try {
      const { authId, recipientId }: IRootState = rootState;
      const {
        document: { id: documentId, recipients },
      }: IDocumentState = state;
      const recipient = getRecipientById(recipientId, recipients);
      await service.pushStatusToPusher(documentId, recipient.id, authId, status);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionSendEventLog]({ rootState, state }, { event }: { event: EDocumentEvent }) {
    try {
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = state;
      await service.logEvent(documentId, recipientId, authId, event);
    } catch (error) {
      console.error(error);
    }
  },
  async [actionUploadAttachment](
    { commit, dispatch },
    {
      uploadAttachment: { documentId, recipientId, authId, attachmentId, file },
      document,
    }: { uploadAttachment: IUploadAttachment; document: IDocument }
  ) {
    try {
      commit(mutationSetUploadingAttachment, attachmentId);
      await AttachmentService.upload(documentId, recipientId, authId, attachmentId, file);
      commit(mutationRemoveUploadingAttachment, attachmentId);
      dispatch(actionUpdateAttachments, document);
    } catch (e) {
      dispatch(actionUploadAttachmentFailed, attachmentId);
    }
  },
  async [actionUploadAttachmentFailed]({ commit, dispatch }, attachmentId: string) {
    commit(mutationRemoveUploadingAttachment, attachmentId);
    dispatch('alert/showDangerAlert', translate('failed-to-upload-attachment'), {
      root: true,
    });
  },
  [actionApproveDocument]({ commit }, { recipientId }: { recipientId: string }) {
    const status: ERecipientStatus = ERecipientStatus.Approved;
    commit(mutationSetRecipientStatus, { recipientId, status });
  },
  [actionExpiredReload](_, { expirationDate }: IDocument) {
    const timeout: number = moment(expirationDate).diff(moment());
    const maxTimeout: number = 8 * 3600 * 1000;
    if (timeout > 0) {
      setTimeout(() => {
        window.location.reload();
      }, Math.min(timeout, maxTimeout));
    }
  },
  async [actionForwardToRecipient](
    { rootState, state, dispatch, commit },
    recipientInfo: IForwardRecipientInfo
  ) {
    try {
      commit(mutationSetButtonLoading, true);
      const { recipientId, authId }: IRootState = rootState;
      const {
        document: { id: documentId },
      }: IDocumentState = state;
      const response: { status: number; error: string } = await service.forward(
        documentId,
        recipientId,
        authId,
        recipientInfo
      );

      if (response.status === 1) {
        dispatch(actionForwardToRecipientSuccess, recipientInfo);
      } else {
        dispatch(actionForwardToRecipientFailed);
      }
    } catch (e) {
      dispatch(actionForwardToRecipientFailed);
    }
  },
  async [actionForwardToRecipientSuccess](
    { dispatch, commit },
    recipientInfo: IForwardRecipientInfo
  ) {
    dispatch(actionSendPusherStatus, { status: EDocumentEvent.Forwarded });
    dispatch(actionSendEventLog, { event: EDocumentEvent.Forwarded });
    if (recipientInfo.transferSignature) {
      dispatch(`${fieldNamespace}/${actionLoadFields}`, { root: true });
    }
    dispatch(actionSetForwardStatus, true);

    const recipients: IRecipient[] = await service.getRecipients();
    commit(mutationUpdateRecipients, recipients);
    commit(mutationSetButtonLoading, false);
  },
  [actionForwardToRecipientFailed]({ dispatch, commit }) {
    dispatch('alert/showDangerAlert', translate('failed-to-invite-recipient'), {
      root: true,
    });
    commit(mutationSetButtonLoading, false);
  },
  [actionSetForwardStatus]({ commit }, forwarded: boolean) {
    commit(mutationSetForwarded, forwarded);
  },
  async [actionGetPdfUrl]({ dispatch }) {
    try {
      const pdfUrl: string = await service.getDownloadUrl();
      dispatch(actionGetPdfUrlSuccess, pdfUrl);
    } catch (e) {
      dispatch(actionGetPdfUrlFailed);
    }
  },
  [actionGetPdfUrlSuccess]({ commit }, pdfUrl: string) {
    commit(mutationSetPdfUrl, pdfUrl);
  },
  [actionGetPdfUrlFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('failed-to-download-pdf'), {
      root: true,
    });
  },
  [actionSetDocumentStatus]({ commit }, status: EDocumentStatus) {
    commit(mutationSetDocumentStatus, status);
  },
};
